// Hozzárendelés-lezárás dialógus — a billing+recurring assignment `billingEndDate`-jét kéri be (a ConfirmDialog
// nem hordoz mező-bevitelt, ezért dedikált). A dátumot a KÖZÖS DatePicker adja (YYYY-MM-DD string). A megerősítés
// a billingEndDate-tel süt (a route a serviceAssignmentClose action-t hívja If-Match/OCC-vel). Mentés közben a
// gombok disabled.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** ISO-date (YYYY-MM-DD) — a helyi „ma" (nem UTC, nincs nap-csúszás). */
function todayIso(now: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export interface ServiceAssignmentCloseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A lezárandó hozzárendelés neve (a leírásban) + a jelenlegi billingEndDate (előtöltés). */
  serviceName?: string | null | undefined;
  initialEndDate?: string | null | undefined;
  pending?: boolean;
  onConfirm: (billingEndDate: string) => void;
}

export function ServiceAssignmentCloseDialog({
  open,
  onOpenChange,
  serviceName,
  initialEndDate,
  pending = false,
  onConfirm,
}: ServiceAssignmentCloseDialogProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [endDate, setEndDate] = useState('');

  // A dialógus nyitásakor az előtöltés: a jelenlegi lezárás-dátum vagy a mai nap.
  useEffect(() => {
    if (open) setEndDate(initialEndDate && initialEndDate !== '' ? initialEndDate : todayIso());
  }, [open, initialEndDate]);

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('serviceAssignments.closeTitle')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <DialogDescription>
            {t('serviceAssignments.closeDesc', { name: serviceName ?? '' })}
          </DialogDescription>
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="close-end-date">
              {t('serviceAssignments.closeEndDate')}
            </label>
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              label={t('serviceAssignments.closeEndDate')}
              language={i18n.language}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t('serviceAssignments.cancel')}
          </Button>
          <Button
            variant="brand"
            type="button"
            onClick={() => onConfirm(endDate)}
            disabled={pending || endDate === ''}
          >
            {pending ? t('serviceAssignments.closing') : t('serviceAssignments.closeConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
